import type { CustomAttribute, Primitive } from "@similarweb/sw-track";

import { swSettings } from "app/common/services/swSettings";
import type { ImportSourceType } from "app/components/import-source/types";
import type {
    ICampaignsConsumptionMetrics,
    IKeywordsCampaignsWizardCompetitor,
    IMyUserConsumptionMetrics,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import type {
    CampaignsPagesError,
    ECampaignPagesError,
    ICampaignsFieldsCollection,
    ICampaignsWizardState,
} from "app/pages/digital-marketing/keywords-campaigns/wizard/campaigns-wizard/types";
import type { ISimilarSiteSuggestion } from "app/pages/digital-marketing/keywords-campaigns/wizard/constants";
import {
    wizardErrorTooltipMapping,
    wizardRankTrackerErrorMessagesMapping,
} from "app/pages/digital-marketing/keywords-campaigns/wizard/constants";
import { EAssetType } from "app/UtilitiesAndConstants/types/assetType";
import { fetchSimilarSitesQuery } from "app/services/sitesService/queries/sitesQueries";

export const getWizardRankTrackerErrorMsg = (errorAsEnum: ECampaignPagesError | null): string | undefined => {
    if (!errorAsEnum) {
        return undefined;
    }

    return wizardRankTrackerErrorMessagesMapping[errorAsEnum];
};

export const wizardErrorTooltipContent = (errorAsEnum: ECampaignPagesError | null): string | undefined => {
    if (!errorAsEnum) {
        return undefined;
    }

    return wizardErrorTooltipMapping[errorAsEnum];
};

const getPageErrorInWizard = (
    errorMessage: CampaignsPagesError,
    pageFields: ICampaignsFieldsCollection,
    updatedFieldName: string,
    pageError: CampaignsPagesError,
): CampaignsPagesError => {
    const othersErrors = Object.entries(pageFields).filter((filed) => {
        const [fieldName, fieldData] = filed;
        return fieldName !== updatedFieldName ? fieldData.hasError : false;
    });
    return othersErrors.length ? pageError : errorMessage;
};

/**
 * @deprecated use produce from immer and setWizardState
 */
export const getUpdatedWizardState = (
    wizardState: ICampaignsWizardState,
    activePageIndex: number,
    updatedFieldName: string,
    updatedFieldValue: unknown,
    errorMessage?: CampaignsPagesError | null,
): ICampaignsWizardState => {
    const activePage = wizardState.pages[activePageIndex];
    const pageError = getPageErrorInWizard(
        errorMessage ?? null,
        activePage.fields,
        updatedFieldName,
        activePage.pageError ?? null,
    );
    return {
        ...wizardState,

        // @ts-expect-error do not use this metod, use produce from immer
        pages: wizardState.pages.map((page, pageIndex) => {
            if (pageIndex === activePageIndex) {
                return {
                    ...page,
                    fields: {
                        ...page.fields,
                        [updatedFieldName]: { value: updatedFieldValue, hasError: !!errorMessage },
                    },
                    pageError: pageError,
                };
            }
            return page;
        }),
    };
};

const getUrlDomain = (url: string): string => {
    const parsedUrl = url.toLowerCase()?.trim()?.replace("http://", "")?.replace("https://", "")?.split("?")?.[0];
    const domain = parsedUrl?.match(/^(.*:\/\/)?(www\.)?([.\-_a-zA-Z0-9]*)(\/?).*$/);

    return domain?.[3] ?? "";
};

const getFilteredSuggestions = (
    newSuggestions: ISimilarSiteSuggestion[],
    existingSuggestions: IKeywordsCampaignsWizardCompetitor[],
): ISimilarSiteSuggestion[] => {
    return newSuggestions?.filter(({ Domain }) => {
        const index = existingSuggestions.findIndex((item) => item.id === Domain);
        return index === -1;
    });
};

const getCompetitorsList = (suggestedCompetitors: ISimilarSiteSuggestion[]): IKeywordsCampaignsWizardCompetitor[] => {
    return suggestedCompetitors?.map(({ Domain, Favicon }) => {
        return {
            name: Domain,
            icon: Favicon,
            type: EAssetType.Website,
            id: Domain,
        };
    });
};

export const getSuggestedCompetitors = async (url: string): Promise<ISimilarSiteSuggestion[]> => {
    try {
        const domain = getUrlDomain(url);
        return await fetchSimilarSitesQuery(domain, { limit: 30 });
    } catch {
        return [];
    }
};

export const getMergedCompetitors = (
    existingCompetitors: IKeywordsCampaignsWizardCompetitor[],
    similarSites: ISimilarSiteSuggestion[],
): IKeywordsCampaignsWizardCompetitor[] => {
    const suggestedCompetitors = getFilteredSuggestions(similarSites, existingCompetitors);
    const newCompetitors = getCompetitorsList(suggestedCompetitors);
    if (newCompetitors) {
        return [...newCompetitors, ...existingCompetitors];
    } else {
        return [...existingCompetitors];
    }
};

export const getMaxAmountOfRemainingKeywordsForRankTracker = (
    campaignsConsumptionMetrics: ICampaignsConsumptionMetrics,
    myUserConsumptionMetrics: IMyUserConsumptionMetrics,
): number => {
    const consumptionData = swSettings.user.isInternalUser ? myUserConsumptionMetrics : campaignsConsumptionMetrics;
    const totalKeywords = swSettings.user.isInternalUser
        ? swSettings.components.RankTracker.resources.MaxKeywordInternalRankTracker
        : campaignsConsumptionMetrics?.totalKeywords;
    return totalKeywords - consumptionData?.usedKeywords;
};

const allowedCharsRegex = /[^\w \-–—'().\p{L}]/gu;
const boundaryCharsRegex = /^[^\p{L}0-9]+|[^\p{L}0-9]+$/gu;

export function sanitizeCampaignName(name: string = ""): {
    sanitized: string;
    hasIllegalChars: boolean;
    hasBoundaryIssues: boolean;
} {
    // Check if there are illegal characters or boundary issues
    const hasIllegalChars = allowedCharsRegex.test(name);
    const hasBoundaryIssues = boundaryCharsRegex.test(name);

    // Create sanitized version
    const trimmed = name.trim();
    const withoutIllegalChars = trimmed.replace(allowedCharsRegex, "");
    const withFixedBoundaries = withoutIllegalChars.replace(boundaryCharsRegex, "");
    const sanitized = withFixedBoundaries.substring(0, 100);

    return {
        sanitized,
        hasIllegalChars,
        hasBoundaryIssues,
    };
}

export const getLastStepTrackEventProps = (wizardState: ICampaignsWizardState): Record<CustomAttribute, Primitive> => {
    const [, searchEnginesStep, keywordsStep] = wizardState.pages;
    const searchEngines = searchEnginesStep.fields.searchEngines.value;
    const sources = keywordsStep.fields.keywordSources.value;

    const getSourceStats = (type: ImportSourceType) => {
        const sourcesOfType = sources.filter((s) => s.type === type);
        const keywords = sourcesOfType.flatMap((s) => s.keywords);
        return {
            keywords: keywords.length,
            searches: keywords.reduce((acc, kw) => acc + kw.searchEnginesIds.length, 0),
        };
    };

    const totalKeywords = sources.flatMap((s) => s.keywords);
    const totalSearchesCount = totalKeywords.reduce((acc, kw) => acc + kw.searchEnginesIds.length, 0);
    const gsc = getSourceStats("gsc");
    const csv = getSourceStats("csv");
    const list = getSourceStats("list");

    return {
        searchEngines: searchEngines.length,
        totalTrackedKeywords: totalKeywords.length,
        totalTrackedSearches: totalSearchesCount,
        trackedKeywordsGsc: gsc.keywords,
        trackedSearchesGsc: gsc.searches,
        trackedKeywordsCsv: csv.keywords,
        trackedSearchesCsv: csv.searches,
        trackedKeywordsList: list.keywords,
        trackedSearchesList: list.searches,
    };
};
